import React, { useEffect } from "react";
import { Box, Typography } from "@material-ui/core";
import { DateRangePicker, usePrevious } from "halifax";
import dayjs from "dayjs";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";

import { MonthAndDatePickerConnectorProps } from "./container";
import "./styles.scss";

export interface IMonthAndDatePickerProps
  extends MonthAndDatePickerConnectorProps {
  viewType: MonthAndDatePickerType;
  header?: JSX.Element | string;
  // focusedMonthIndex must be given in order to make columnView focus on an arbitrary month
  focusedMonthIndex?: number;
  // setFocusedMonthIndex must be given for working with the groupView + columnView combination
  setFocusedMonthIndex?: (index: number) => void;
  setFromDate?: (date: Date | null) => void;
  setUntilDate?: (date: Date | null) => void;
  from: Date | null;
  until: Date | null;
  className?: string;
  unavailableDates?: Date[];
  enableEvents?: boolean; // Only send events when this is true (for search page)
}

export const MonthAndDatePicker = (props: IMonthAndDatePickerProps) => {
  const {
    pickerType,
    from,
    until,
    setFromDate,
    setUntilDate,
    header,
    viewType,
    className,
    unavailableDates,
    enableEvents = false,
  } = props;

  const previousFrom = usePrevious(from);
  const previousUntil = usePrevious(until);

  useEffect(() => {
    if (enableEvents && !(from && until) && previousFrom && previousUntil) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_RESET,
        funnel: TravelAppInterfaceFunnelType.PREMIUM_STAYS,
      });
    }
  }, [from, until, enableEvents, previousFrom, previousUntil]);

  useEffect(() => {
    if (enableEvents && from && previousFrom !== from) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.PREMIUM_STAYS,
        data: {
          startDate: dayjs(from).format("YYYY-MM-DD"),
        },
      });
    }
  }, [from, previousFrom, enableEvents]);

  useEffect(() => {
    if (enableEvents && until && previousUntil !== until) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.PREMIUM_STAYS,
        data: {
          endDate: dayjs(until).format("YYYY-MM-DD"),
        },
      });
    }
  }, [until, previousUntil, enableEvents]);

  useEffect(() => {
    if (enableEvents && from && until && (previousFrom !== from || previousUntil !== until)) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_FINALIZED,
        funnel: TravelAppInterfaceFunnelType.PREMIUM_STAYS,
      });
    }
  }, [from, until, previousFrom, previousUntil, enableEvents]);

  return (
    <>
      {header && (
        <Box className="month-and-date-picker-header-container">
          {typeof header === "string" ? (
            <Typography variant="body2">{header}</Typography>
          ) : (
            header
          )}
        </Box>
      )}
      <DateRangePicker
        className={className}
        minAllowedDate={dayjs().toDate()}
        maxAllowedDate={dayjs().add(1, "year").toDate()}
        minNumDays={1}
        startDate={from}
        endDate={until}
        setStartDate={setFromDate}
        setEndDate={setUntilDate}
        months={[]}
        priceTags={[]}
        pickerType={pickerType}
        unavailableDates={unavailableDates}
        columnView={viewType === MonthAndDatePickerType.Column}
      />
    </>
  );
};

export enum MonthAndDatePickerType {
  Horizontal,
  Column,
  Group,
}
